import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { FiArrowRight, FiCheckCircle, FiInstagram } from 'react-icons/fi'
import { salonAPI, resolveMediaUrl } from '../services/api'
import ServiceCard from '../components/ServiceCard'
import LoadingSpinner from '../components/LoadingSpinner'
import { useStudio } from '../context/StudioContext'

export default function Home() {
  const { studio, loading: studioLoading } = useStudio()
  const [services, setServices] = useState([])
  const [gallery, setGallery] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [servicesRes, galleryRes] = await Promise.all([
          salonAPI.getServices(),
          salonAPI.getGallery(),
        ])
        setServices(servicesRes.data.slice(0, 4))
        setGallery(galleryRes.data.slice(0, 4))
      } catch (error) {
        console.error('Error fetching data:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [])

  if (loading && studioLoading) {
    return <LoadingSpinner />
  }

  return (
    <div className="pb-20">
      <section className="section-shell grid gap-12 py-14 lg:grid-cols-[1.15fr_0.85fr] lg:items-center lg:py-20">
        <div>
          <p className="eyebrow">Tampa nail and laser studio</p>
          <h1 className="mt-5 font-heading text-5xl leading-none text-stone-900 sm:text-6xl lg:text-7xl">
            {studio?.heroTitle || 'Nails, beauty, and laser care shaped around visible results.'}
          </h1>
          <p className="mt-6 max-w-2xl text-base leading-8 text-stone-600 sm:text-lg">
            {studio?.heroDescription}
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            {(studio?.bioLines || []).map((line) => (
              <span key={line} className="rounded-full border border-stone-200 bg-white/85 px-4 py-2 text-xs font-semibold uppercase tracking-[0.16em] text-stone-600 shadow-sm">
                {line}
              </span>
            ))}
          </div>
          <div className="mt-10 flex flex-wrap gap-4">
            <Link to="/booking" className="primary-button">
              Book an Appointment
            </Link>
            <a
              href={studio?.instagramUrl || 'https://www.instagram.com/maya__nails/'}
              target="_blank"
              rel="noopener noreferrer"
              className="secondary-button inline-flex items-center gap-2"
            >
              <FiInstagram />
              Visit Instagram
            </a>
          </div>
          <div className="mt-10 grid gap-4 sm:grid-cols-3">
            {(studio?.stats || []).map((stat) => (
              <div key={stat.label} className="surface-card p-5">
                <p className="text-xs uppercase tracking-[0.18em] text-stone-500">{stat.label}</p>
                <p className="mt-2 font-heading text-4xl text-stone-900">{stat.value}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="relative">
          <div className="absolute inset-x-4 top-8 h-72 rounded-full bg-[radial-gradient(circle,_rgba(188,119,87,0.28),_rgba(188,119,87,0))]" />
          <div className="surface-card relative overflow-hidden p-4 sm:p-6">
            <div className="grid gap-4 sm:grid-cols-[0.9fr_1.1fr]">
              <div className="overflow-hidden rounded-[2rem] bg-stone-100">
                <img
                  src={resolveMediaUrl(studio?.profileImageUrl || '/instagram/profile.jpg')}
                  alt={studio?.name || 'Maya Nails Spa'}
                  className="h-full min-h-64 w-full object-cover"
                />
              </div>
              <div className="grid gap-4">
                {gallery.slice(0, 2).map((image) => (
                  <div key={image.id} className="group relative overflow-hidden rounded-[2rem]">
                    <img
                      src={resolveMediaUrl(image.imageUrl)}
                      alt={image.title}
                      className="h-40 w-full object-cover transition duration-500 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                    <div className="absolute inset-x-0 bottom-0 p-4 text-white">
                      <p className="text-xs uppercase tracking-[0.18em] text-white/70">{image.category}</p>
                      <p className="mt-1 font-heading text-2xl leading-none">{image.title}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="section-shell py-10">
        <div className="grid gap-6 lg:grid-cols-3">
          {(studio?.promises || []).map((promise) => (
            <article key={promise.title} className="surface-card p-8">
              <FiCheckCircle className="text-2xl text-rose-700" />
              <h2 className="mt-6 font-heading text-4xl text-stone-900">{promise.title}</h2>
              <p className="mt-4 text-sm leading-7 text-stone-600">{promise.description}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="section-shell page-section">
        <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <p className="eyebrow">Featured services</p>
            <h2 className="mt-4 font-heading text-5xl text-stone-900">What the studio is known for</h2>
          </div>
          <Link to="/services" className="inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.18em] text-stone-700">
            View all services
            <FiArrowRight />
          </Link>
        </div>

        {loading ? (
          <LoadingSpinner />
        ) : (
          <div className="grid gap-6 lg:grid-cols-2 xl:grid-cols-4">
            {services.map((service) => (
              <ServiceCard key={service.id} service={service} minimal />
            ))}
          </div>
        )}
      </section>

    </div>
  )
}
